import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import { use2FA } from '../../hooks/use2FA';

interface Setup2FADialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (recoveryCodes: string[]) => void;
}

export const Setup2FADialog: React.FC<Setup2FADialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [verificationCode, setVerificationCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const { setup, enable, setupData, isSettingUp, isEnabling } = use2FA();

  const steps = ['Scan QR Code', 'Verify Code', 'Save Recovery Codes'];

  useEffect(() => {
    if (open && activeStep === 0 && !setupData) {
      handleSetup();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSetup = async () => {
    try {
      setError(null);
      await setup();
      setActiveStep(1);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to generate QR code');
    }
  };

  const handleVerifyAndEnable = async () => {
    try {
      setError(null);
      const response = await enable(verificationCode);
      setRecoveryCodes(response.recoveryCodes);
      setActiveStep(2);
      if (onSuccess) {
        onSuccess(response.recoveryCodes);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Invalid verification code');
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    setVerificationCode('');
    setRecoveryCodes([]);
    setError(null);
    onClose();
  };

  const downloadRecoveryCodes = () => {
    const blob = new Blob([recoveryCodes.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'recovery-codes.txt';
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Enable Two-Factor Authentication</DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {activeStep === 0 && isSettingUp && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {activeStep === 1 && setupData && (
          <Box>
            <Typography variant="body2" gutterBottom>
              1. Install an authenticator app on your phone (Google Authenticator, Authy, etc.)
            </Typography>
            <Typography variant="body2" gutterBottom>
              2. Scan this QR code with your authenticator app:
            </Typography>
            <Box display="flex" justifyContent="center" my={2}>
              <img src={setupData.qrCode} alt="2FA QR Code" style={{ width: 200, height: 200 }} />
            </Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Or manually enter this secret key: {setupData.secret}
            </Typography>
            <Typography variant="body2" sx={{ mt: 2 }} gutterBottom>
              3. Enter the 6-digit code from your authenticator app:
            </Typography>
            <TextField
              fullWidth
              label="Verification Code"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              inputProps={{ maxLength: 6 }}
              sx={{ mt: 1 }}
            />
          </Box>
        )}

        {activeStep === 2 && (
          <Box>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Save these recovery codes in a safe place. You'll need them to access your account if
              you lose your authenticator device.
            </Alert>
            <Box
              sx={{
                p: 2,
                bgcolor: 'grey.100',
                borderRadius: 1,
                fontFamily: 'monospace',
              }}
            >
              {recoveryCodes.map((code, index) => (
                <Typography key={index}>{code}</Typography>
              ))}
            </Box>
            <Button
              variant="outlined"
              fullWidth
              onClick={downloadRecoveryCodes}
              sx={{ mt: 2 }}
            >
              Download Recovery Codes
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {activeStep === 2 ? (
          <Button onClick={handleClose}>Done</Button>
        ) : (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            {activeStep === 1 && (
              <Button
                onClick={handleVerifyAndEnable}
                variant="contained"
                disabled={verificationCode.length !== 6 || isEnabling}
              >
                {isEnabling ? 'Verifying...' : 'Enable 2FA'}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
